/**
 * HelpPopover — the one click-to-open explanation layer (#602).
 *
 * The app already had two tooltip systems before this file: `atoms/Tooltip`
 * (hover/focus, hover-only discovery, no dismissal contract) and cardKit's
 * `InfoTooltip` (the click-toggled ⓘ card beside Open Positions / Open Orders,
 * which hand-rolled its own outside-click and Escape listeners). #602 needed a
 * third caller — the fee-uncertainty marker on price pills — and explicitly
 * forbade a third system, so `InfoTooltip` and `FeeScheduleMarker` are both
 * thin wrappers over this primitive. Look identical to what /positions shipped
 * before; the mechanics underneath are the shared ones.
 *
 * Two things this owns that the old InfoTooltip did not:
 *
 * 1. **The panel is portalled and `fixed`.** InfoTooltip's `absolute` panel was
 *    fine hanging off a section heading with the whole page below it. The fee
 *    marker lives inside ladder cells and table rows — surfaces with `overflow`
 *    ancestors and a neighbouring price 40px to the right — where an in-flow
 *    absolute panel is either clipped or covering the number the trader is
 *    reading. Fixed + viewport-clamped coordinates cost zero layout width, so
 *    a popover can never reflow a row or expand a cell.
 *
 * 2. **Dismissal is `useDismissibleLayer`,** not a private pair of listeners.
 *    That hook's header names cardKit's InfoTooltip as one of five surfaces
 *    that still hand-rolled dismissal; this migrates it.
 *
 * Focus deliberately stays on the trigger while the panel is open: the body is
 * static prose with nothing to operate, so moving focus into it would only give
 * a keyboard user somewhere extra to tab back out of. Escape returns focus to
 * the trigger; an outside click does not (it would yank focus off whatever the
 * user just clicked — the distinction `useDismissibleLayer` passes a reason for).
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';

/** Gap between the trigger and the panel edge, in px — `InfoTooltip`'s
 *  original `mt-2`, kept to the pixel so /positions looks untouched. */
const PANEL_GAP = 8;
/** Keep the panel this far from every viewport edge, in px. */
const VIEWPORT_MARGIN = 8;
/** Default panel width — wide enough for a paragraph, still viewport-clamped. */
const DEFAULT_PANEL_WIDTH = 440;
/**
 * Never squeeze the panel below this height even when the chosen side is
 * shorter than it; the body scrolls instead. A 40px-tall panel would be
 * technically on-screen and practically unreadable.
 */
const MIN_PANEL_HEIGHT = 120;
/**
 * The floor above wins over the viewport when neither side has 120px to give
 * — under ~260px of viewport height, or under `panelWidth + 16` of width, the
 * panel is clipped rather than shrunk. Deliberate: at those sizes an unclipped
 * panel would be too short to read a sentence in. The supported floor is the
 * desktop set this ships against (1470x956 and up).
 */

interface PanelPosition {
  left: number;
  /** Exactly one of `top` / `bottom` is set — whichever edge is anchored. */
  top?: number;
  bottom?: number;
  maxHeight: number;
  width: number;
}

/**
 * Place the panel against the trigger without ever measuring the panel.
 *
 * Anchoring the panel's TOP edge when it opens downward and its BOTTOM edge
 * when it opens upward is what removes the measurement: the side with more
 * room wins, and `maxHeight` is exactly the room on that side, so the panel
 * cannot overflow the viewport whatever its content turns out to be. A
 * measure-then-reposition pass would need a second render and could flicker.
 *
 * Exported for the positioning tests; not part of the component's API.
 */
export function helpPanelPosition(
  rect: { top: number; bottom: number; left: number },
  width: number,
  viewport: { width: number; height: number },
): PanelPosition {
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewport.width - width - VIEWPORT_MARGIN);
  const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), maxLeft);
  const roomBelow = viewport.height - rect.bottom - PANEL_GAP - VIEWPORT_MARGIN;
  const roomAbove = rect.top - PANEL_GAP - VIEWPORT_MARGIN;
  return roomBelow >= roomAbove
    ? {
        left,
        top: rect.bottom + PANEL_GAP,
        maxHeight: Math.max(roomBelow, MIN_PANEL_HEIGHT),
        width,
      }
    : {
        left,
        bottom: viewport.height - rect.top + PANEL_GAP,
        maxHeight: Math.max(roomAbove, MIN_PANEL_HEIGHT),
        width,
      };
}

export interface HelpPopoverProps {
  /**
   * Accessible name of the trigger button. Required: the trigger is usually a
   * glyph, so this is the only name a screen reader has. It also names the
   * panel when no `title` is given.
   */
  label: string;
  /** Short heading at the top of the panel. */
  title?: string;
  /** Visual content of the trigger — an icon or a badge glyph. */
  trigger: ReactNode;
  /** Classes for the trigger button. Callers own the trigger's whole look. */
  triggerClassName?: string;
  /** Panel body. Prose, not controls — see the focus note in the file header. */
  children: ReactNode;
  /**
   * Plain-text copy of the explanation, kept permanently in the DOM behind
   * `sr-only` and named by the trigger's `aria-describedby`.
   *
   * Without it the trigger can only point at the panel, and only while the
   * panel exists — a description that appears on an ALREADY-focused element,
   * which screen readers generally do not re-announce. The reader would be
   * told a button expanded and left to find the text at the far end of the
   * document, where the portal put it. Describing the trigger unconditionally
   * puts the explanation on the reliable path instead: read on focus arrival,
   * before the trader has to decide whether activating is worth it.
   *
   * Opt-in per caller because it is only right for SHORT copy. The fee marker
   * is two sentences; `InfoTooltip`'s column glossaries would be read out in
   * full every time a keyboard user tabbed past the ⓘ, so they stay on the
   * panel-only association.
   */
  srDescription?: string;
  /** Panel width in px. Used for both the rendered width and the clamp math. */
  panelWidth?: number;
  /** Classes for the wrapper that the trigger sits in. */
  className?: string;
  /** Test hook on the trigger button. */
  testId?: string;
}

export function HelpPopover({
  label,
  title,
  trigger,
  triggerClassName,
  children,
  srDescription,
  panelWidth = DEFAULT_PANEL_WIDTH,
  className,
  testId,
}: HelpPopoverProps) {
  const panelId = useId();
  const srDescriptionId = `${panelId}-sr`;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // The trigger scrolled off screen — the panel would be explaining a price
    // the trader can no longer see, so it goes with it.
    if (
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth
    ) {
      setOpen(false);
      return;
    }
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const width = Math.min(panelWidth, Math.max(200, viewport.width - VIEWPORT_MARGIN * 2));
    setPosition(helpPanelPosition(rect, width, viewport));
  }, [panelWidth]);

  // Layout effect, not a plain effect: the panel is painted at these
  // coordinates on the same frame it mounts, so it never flashes at 0,0.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, reposition]);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useDismissibleLayer({
    open,
    // Restore focus on Escape ONLY while the trigger still holds it. Every
    // open layer hears Escape on `document`, so without this guard a trader
    // pressing Escape to clear a size field three rows away would have focus
    // yanked out of that field and onto a fee marker.
    onDismiss: (reason) =>
      close(reason === 'escape' && triggerRef.current === document.activeElement),
    refs: [triggerRef, panelRef],
    // Keyboard activation of a button fires `click` with NO `mousedown`, so
    // the outside-pointer listener never sees a keyboard user move from one
    // trigger to the next: Tab-Enter-Tab-Enter left two panels open at once.
    // Closing when focus leaves costs nothing here — the panel is prose with
    // nothing focusable inside it, so tabbing off the trigger always means
    // leaving, never moving in to read.
    dismissOnFocusOut: true,
  });

  // A `fixed` panel is pinned to the viewport, so a scroll under it would
  // otherwise leave it hanging over unrelated rows. It follows its trigger
  // instead of closing: closing on scroll races with the browser's own
  // scroll-into-view — a marker below the fold reached by Tab (or by a click
  // near the edge) scrolls first and would have shut the panel on the way in.
  // Capture phase because these markers live inside scrolling ladders, and a
  // container's scroll event does not bubble.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  const panel =
    open && position
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label={title ?? label}
            className="bg-card border-border fixed z-[100] overflow-y-auto rounded-lg border p-3 shadow-lg"
            style={{
              left: position.left,
              top: position.top,
              bottom: position.bottom,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
            // The panel is portalled to <body>, so it is outside the DOM of
            // whatever row or pill opened it — but a click inside it must not
            // reach an enclosing layer's dismissal listener either.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <div className="text-foreground mb-2 text-sm font-semibold">{title}</div>
            )}
            <div className="text-muted-foreground text-xs leading-relaxed">{children}</div>
          </div>,
          document.body,
        )
      : null;

  return (
    // No `relative` here: nothing inside the wrapper is absolutely positioned
    // (the panel is portalled), and a base `position` would depend on twMerge
    // dropping it for the callers that position the wrapper themselves —
    // plain concatenation would leave the marker in the flex flow instead,
    // which is exactly the horizontal reflow #602 forbids.
    <span className={cn('inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        // Asking for an explanation must never be able to place an order. The
        // LT2 boundary is painted on a whole focused row, and these triggers
        // sit ON the price inside it, where a bare button hands the next A/D
        // press back to the card — see `ownedByInteractive` in
        // components/Lt2HotkeyListener.tsx. This claims the keystroke instead,
        // so A/D refuses out loud and Escape closes the panel without also
        // clearing the trader's card aim.
        data-lt2-owner=""
        // Prefer the always-present copy: it is read when focus ARRIVES, which
        // is the association screen readers actually honour. The panel is the
        // fallback for callers whose copy is too long to hear on every Tab,
        // and it can only be pointed at while it exists.
        aria-describedby={
          srDescription ? srDescriptionId : open ? panelId : undefined
        }
        className={triggerClassName}
        // Stop the CLICK only. These triggers sit inside click-to-trade pills,
        // ladder rows and expandable table rows, and asking for help must never
        // dispatch an order — but `mousedown` has to keep bubbling to
        // `document` or the shared dismissal contract stops seeing it (React
        // calls the native `stopPropagation` too, so stopping it here would
        // leave a second popover's trigger unable to close the first).
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {trigger}
      </button>
      {/* `sr-only` is position:absolute at 1x1 — no layout box, so this cannot
          widen a price cell or grow a row. */}
      {srDescription && (
        <span id={srDescriptionId} className="sr-only">
          {srDescription}
        </span>
      )}
      {panel}
    </span>
  );
}
